// Capa HTTP de la asistencia de redacción (RF-E2). Tipos + funciones fetch;
// el estado vive en TanStack Query (ver `src/hooks/useAsistenciaDeRedaccion.ts`).

import { apiFetch } from "./api";

/**
 * Códigos de campo con asistencia de redacción.
 *
 * Vocabulario CERRADO y no una string cualquiera: el código viaja al servidor
 * y ahí elige qué contexto se le suma al prompt. Un valor inventado no rompe
 * —cae en el prompt base—, pero sí produce el defecto silencioso de que un
 * campo quede sin su contexto porque alguien escribió mal la clave.
 *
 * OJO: identifican el campo como TIPO, nunca al dato de un informe concreto.
 * Meterle el id del informe generaría un prompt por informe en vez de uno por
 * campo (es la forma que usa el prototipo, donde la clave solo sigue el
 * estado de la UI).
 */
export const CAMPOS_CON_ASISTENCIA = {
  descripcionUbicacion: "informe.descripcion_ubicacion",
  descripcion: "informe.descripcion",
  notaImagen: "informe.nota_imagen",
  /**
   * Los deslindes de un rol, en la pestaña "Descripción de la propiedad".
   *
   * ES UN CÓDIGO Y NO UNO POR ROL, como todos: identifican el campo como TIPO,
   * nunca al dato de un informe concreto. Un informe con nueve roles usa nueve
   * veces el mismo contexto, que es justamente lo que hace útil escribirlo una
   * sola vez en `/admin/ia`.
   */
  descripcionDeslindes: "informe.descripcion_deslindes",
  /**
   * La prosa general sobre la maquinaria del predio, en su pestaña.
   *
   * Es UNA sola por informe, a diferencia de los deslindes: describe el
   * conjunto y no cada fila. La tabla de máquinas no lleva asistencia — lo que
   * hay ahí son cifras, no redacción.
   */
  descripcionMaquinaria: "informe.descripcion_maquinaria",
  /**
   * La descripción general de los suelos, en "Descripción de suelos".
   *
   * Mismo criterio que la de maquinaria: describe el conjunto y no cada fila
   * del cuadro de clases de uso, que son cifras.
   */
  descripcionSuelos: "informe.descripcion_suelos",
  /** La topografía del predio, en la misma pestaña. */
  topografia: "informe.topografia",
  /**
   * La prosa sobre las instalaciones y obras complementarias del predio.
   *
   * Es UNA sola por informe, como la de maquinaria: describe el conjunto y no
   * cada fila. El cuadro no lleva asistencia — lo que hay ahí son cifras, no
   * redacción.
   */
  descripcionInstalaciones: "informe.descripcion_instalaciones",
  /** La prosa sobre las construcciones del predio. Una sola por informe. */
  descripcionConstrucciones: "informe.descripcion_construcciones",
  /** La prosa sobre las plantaciones del predio. Una sola por informe. */
  descripcionPlantaciones: "informe.descripcion_plantaciones",
} as const;

export type CampoConAsistencia =
  (typeof CAMPOS_CON_ASISTENCIA)[keyof typeof CAMPOS_CON_ASISTENCIA];

export interface PropuestaDeRedaccion {
  /** Id de la corrida, para después decir si se aceptó o se descartó. */
  ejecucion_id: number;
  texto: string;
}

export interface MejorarRedaccionInput {
  texto: string;
  campo: CampoConAsistencia;
  /** Nulo mientras el informe no existe: en "Crear informe" se mejora antes de guardar. */
  informeId?: number;
}

export function mejorarRedaccionRequest({
  texto,
  campo,
  informeId,
}: MejorarRedaccionInput): Promise<PropuestaDeRedaccion> {
  return apiFetch<PropuestaDeRedaccion>("/api/ia/redaccion", {
    method: "POST",
    body: JSON.stringify({ texto, campo, informe_id: informeId ?? null }),
  });
}

export type ResultadoDeLaPropuesta = "aceptado" | "descartado";

/**
 * Registra qué hizo la persona con la propuesta.
 *
 * Es el dato con el que se mide si la asistencia sirve, y no se puede
 * reconstruir después. Por eso se envía también al DESCARTAR: un descarte
 * informa tanto como una aceptación.
 */
export function resolverPropuestaRequest(
  ejecucionId: number,
  resultado: ResultadoDeLaPropuesta,
): Promise<void> {
  return apiFetch<void>(`/api/ia/redaccion/${ejecucionId}`, {
    method: "PATCH",
    body: JSON.stringify({ resultado }),
  });
}

/**
 * Los textos que se redactan desde terreno: las dos descripciones de
 * Antecedentes generales, la descripción general de las construcciones y los
 * dos textos de suelos (descripción general y topografía).
 */
export type CampoRedactableDesdeTerreno =
  | "descripcion_ubicacion"
  | "descripcion"
  | "construcciones"
  | "suelos"
  | "topografia"
  | "aguas"
  | "maquinarias"
  | "instalaciones"
  | "plantaciones";

/**
 * Pide al servidor que redacte una descripción con lo que el encargado mandó
 * desde terreno (textos y notas de voz ya transcritas).
 *
 * DEVUELVE UNA PROPUESTA Y NO ESCRIBE EL INFORME: el texto va al formulario y se
 * guarda con "Guardar cambios". La corrida se resuelve con
 * {@link resolverPropuestaRequest}, el mismo PATCH de la asistencia de redacción.
 */
export function redactarDesdeTerrenoRequest(
  informeId: number,
  campo: CampoRedactableDesdeTerreno,
): Promise<PropuestaDeRedaccion> {
  return apiFetch<PropuestaDeRedaccion>(`/api/informes/${informeId}/terreno/redactar`, {
    method: "POST",
    body: JSON.stringify({ campo }),
  });
}
